import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export const useStickyHeaderOffsets = (headerKeys: string[]) => {
  const headerRefs = useRef<Record<string, HTMLElement | null>>({});
  const [headerOffsets, setHeaderOffsets] = useState<Record<string, number>>(
    {},
  );

  const setHeaderRef = useCallback(
    (key: string) => (node: HTMLElement | null) => {
      headerRefs.current[key] = node;
    },
    [],
  );

  const computeHeaderOffsets = useCallback(() => {
    let offset = 0;
    const nextOffsets: Record<string, number> = {};
    for (const key of headerKeys) {
      nextOffsets[key] = offset;
      const height = headerRefs.current[key]?.offsetHeight ?? 0;
      offset += height;
    }
    setHeaderOffsets(nextOffsets);
  }, [headerKeys]);

  useLayoutEffect(() => {
    computeHeaderOffsets();
  }, [computeHeaderOffsets]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      const handleResize = () => computeHeaderOffsets();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }

    const observer = new ResizeObserver(() => computeHeaderOffsets());
    for (const key of headerKeys) {
      const node = headerRefs.current[key];
      if (node) {
        observer.observe(node);
      }
    }

    return () => observer.disconnect();
  }, [computeHeaderOffsets, headerKeys]);

  const getHeaderOffset = useCallback(
    (key: string) => headerOffsets[key] ?? 0,
    [headerOffsets],
  );

  return { setHeaderRef, getHeaderOffset };
};
